import type { DataValue, ActivityDayBucket } from './lib';
import { formatMetric, niceCountAxis, visiblePoints } from './lib';

type Point = DataValue<number> | null | undefined;
export type ChartSeries = { label: string; color: string; values: Point[] };

type ComboProps = {
  dates: string[];
  bars: ChartSeries[];
  line?: ChartSeries & { axis: 'count' | 'percent' };
  lines?: Array<ChartSeries & { axis: 'count' | 'percent' }>;
  label: string;
  minWidth?: number;
};

const observed = (point: Point): number | null =>
  point?.value !== null && point?.value !== undefined && Number.isFinite(point.value)
    ? point.value : null;

/** Counts share the left axis; an optional rate line uses its own 0–100% right axis. */
export function OperationsComboChart({ dates, bars, line, lines, label, minWidth = 480 }: ComboProps) {
  const lineSeries = lines ?? (line ? [line] : []);
  const hasPercent = lineSeries.some(series => series.axis === 'percent');
  const countValues = [...bars, ...lineSeries.filter(series => series.axis === 'count')]
    .flatMap(series => visiblePoints(series.values.filter((point): point is DataValue<number> => !!point)));
  const rateValues = lineSeries.filter(series => series.axis === 'percent')
    .flatMap(series => visiblePoints(series.values.filter((point): point is DataValue<number> => !!point)));
  if (!dates.length || (!countValues.length && !rateValues.length)) return <span>—</span>;

  const ticks = niceCountAxis(countValues);
  const ceiling = ticks.at(-1) || 1;
  const width = Math.max(minWidth, dates.length * 54 + 74);
  const height = 220;
  const left = 42;
  const right = hasPercent ? 44 : 12;
  const top = 16;
  const bottom = 34;
  const plotWidth = width - left - right;
  const plotHeight = height - top - bottom;
  const step = plotWidth / dates.length;
  const x = (index: number) => left + step * (index + 0.5);
  const countY = (value: number) => top + plotHeight - (value / ceiling) * plotHeight;
  const lineY = (value: number, axis: 'count' | 'percent') => axis === 'percent'
    ? top + plotHeight - (Math.max(0, Math.min(100, value)) / 100) * plotHeight
    : countY(value);
  const barWidth = Math.min(18, Math.max(5, step * 0.55 / Math.max(1, bars.length)));
  const tickEvery = Math.max(1, Math.ceil(dates.length / 7));

  return <div style={{ overflowX: 'auto' }}>
    <svg role="img" aria-label={label} viewBox={`0 0 ${width} ${height}`} style={{ width: dates.length > 10 ? width : '100%', minWidth: dates.length > 10 ? width : undefined, height: 185 }}>
      {ticks.map(tick => <g key={tick}>
        <line x1={left} x2={width - right} y1={countY(tick)} y2={countY(tick)} stroke="#e9edf2" />
        <text x={left - 6} y={countY(tick) + 4} textAnchor="end" fontSize="11" fill="#687487">{tick}</text>
      </g>)}
      {hasPercent && <>
        <text x={width - 4} y={top + 4} textAnchor="end" fontSize="10" fill="#687487">100%</text>
        <text x={width - 4} y={top + plotHeight + 3} textAnchor="end" fontSize="10" fill="#687487">0%</text>
      </>}
      {dates.map((date, index) => (index % tickEvery === 0 || index === dates.length - 1)
        ? <text key={date} x={x(index)} y={height - 9} textAnchor="middle" fontSize="10" fill="#687487">{date.slice(5)}</text> : null)}
      {bars.flatMap((series, seriesIndex) => dates.map((date, index) => {
        const value = observed(series.values[index]);
        if (value === null) return null;
        const offset = (seriesIndex - (bars.length - 1) / 2) * (barWidth + 2);
        const center = x(index) + offset;
        const y = countY(value);
        return value === 0
          ? <circle key={`${series.label}-${date}`} cx={center} cy={y} r="2.5" fill={series.color}><title>{`${date} ${series.label}: 0`}</title></circle>
          : <rect key={`${series.label}-${date}`} x={center - barWidth / 2} y={y} width={barWidth} height={top + plotHeight - y} rx="2" fill={series.color}><title>{`${date} ${series.label}: ${value}`}</title></rect>;
      }))}
      {lineSeries.map(series => {
        const segments: Array<Array<{ x: number; y: number; date: string; value: number }>> = [];
        let current: Array<{ x: number; y: number; date: string; value: number }> = [];
        dates.forEach((date, index) => {
          const value = observed(series.values[index]);
          if (value === null) {
            if (current.length) segments.push(current);
            current = [];
          } else current.push({ x: x(index), y: lineY(value, series.axis), date, value });
        });
        if (current.length) segments.push(current);
        return <g key={series.label}>
          {segments.map((points, index) => <polyline key={index} fill="none" stroke={series.color} strokeWidth="2.5" points={points.map(point => `${point.x},${point.y}`).join(' ')} />)}
          {segments.flat().map(point => <circle key={point.date} cx={point.x} cy={point.y} r="3.5" fill={series.color}><title>{`${point.date} ${series.label}: ${point.value}${series.axis === 'percent' ? '%' : ''}`}</title></circle>)}
        </g>;
      })}
    </svg>
    <div style={{ display: 'flex', justifyContent: 'center', gap: 14, flexWrap: 'wrap', fontSize: 12 }}>
      {[...bars, ...lineSeries].map(series => <span key={series.label}><span style={{ display: 'inline-block', width: 9, height: 9, background: series.color, marginRight: 5 }} />{series.label}</span>)}
    </div>
  </div>;
}

export function ActivityDayBars({ buckets }: { buckets?: ActivityDayBucket[] }) {
  if (!buckets?.length) return <span>—</span>;
  const values = buckets.flatMap(bucket => observed(bucket.members) === null ? [] : [observed(bucket.members)!]);
  const ticks = niceCountAxis(values);
  const ceiling = ticks.at(-1) || 1;
  const width = Math.max(430, buckets.length * 62 + 46);
  const height = 205;
  const left = 40;
  const plotHeight = 145;
  const step = (width - left - 16) / buckets.length;
  const y = (value: number) => 18 + plotHeight - value / ceiling * plotHeight;
  return <div className="operations-chart-scroll">
    <svg role="img" aria-label="使用天數分布" viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', minWidth: 390 }}>
      {ticks.map(tick => <g key={tick}>
        <line x1={left} x2={width - 16} y1={y(tick)} y2={y(tick)} stroke="#e7edf6" />
        <text x={left - 7} y={y(tick) + 4} textAnchor="end" fontSize="11" fill="#637796">{tick}</text>
      </g>)}
      {buckets.map((bucket, index) => {
        const members = observed(bucket.members);
        const center = left + step * (index + 0.5);
        return <g key={bucket.label}>
          {members !== null && <>
            <rect x={center - Math.min(22, step * 0.32)} y={y(members)} width={Math.min(44, step * 0.64)} height={Math.max(0, 18 + plotHeight - y(members))} rx="3" fill="#559bfa" />
            <text x={center} y={Math.max(12, y(members) - 5)} textAnchor="middle" fontSize="11" fill="#16315a">{formatMetric(bucket.members)}</text>
          </>}
          <text x={center} y={height - 23} textAnchor="middle" fontSize="11" fill="#526a8e">{bucket.label}</text>
          <title>{`${bucket.label}：${formatMetric(bucket.members)} 人，${formatMetric(bucket.ratio, '%')}`}</title>
        </g>;
      })}
    </svg>
  </div>;
}
